/**
 * CollabV3ThreadItem 컴포넌트
 * @description 스레드 글 한 줄(앱 `CollabThreadRow`) — 머리(얼굴 · 이름 · `Host` · `Pinned` · `Picked` ·
 *   날짜), 마무리 글 · 집행 결과의 이름표, 본문, 링크, 사진, 고른 작품, 집행 내역, 하트 · 댓글 숫자.
 *   보기 전용이다 — 글 메뉴 · 하트 · 댓글 입력은 없다.
 *
 *   글(사진 포함)을 누르면 앱의 그 글이 열린다(`CollabV3AppOverlay`). 바깥 링크만 그 위에 올려 따로
 *   새 탭으로 연다.
 * @example <CollabV3ThreadItem item={item} subject={subject} />
 */
import { memo } from "react";

import CollabV3AppOverlay from "./CollabV3AppOverlay";
import CollabV3Avatar from "./CollabV3Avatar";
import CollabV3Badge from "./CollabV3Badge";
import CollabV3DateText from "./CollabV3DateText";
import { collabV3ThreadStyles as styles } from "./CollabV3Thread.styles";
import type { CollabV3ThreadItemProps } from "./CollabV3Thread.types";
import {
  CollabV3ThreadExecution,
  CollabV3ThreadMedia,
  CollabV3ThreadPicked,
  CollabV3ThreadReactions,
} from "./CollabV3ThreadExtras";
import CollabV3ThreadLinkRow from "./CollabV3ThreadLinkRow";

import { COLLAB_V3_COPY } from "../../constants/collabV3Copy";
import type { CollabV3ThreadItemType } from "../../dtos";

const copy = COLLAB_V3_COPY.thread;

/** 글 종류 이름표 — 일반 글 · 작품 글은 없다 */
const TYPE_LABELS: Partial<Record<CollabV3ThreadItemType, string>> = {
  WRAP_UP: copy.wrapUp,
  ANNOUNCEMENT: copy.wrapUp,
  FUNDING_EXECUTION: copy.execution,
};

const CollabV3ThreadItem = memo<CollabV3ThreadItemProps>(({ item, subject }) => {
  const label = TYPE_LABELS[item.type];
  return (
    <li className={styles.item}>
      <CollabV3AppOverlay
        subject={subject}
        placement="thread"
        path={item.appPath}
        label={`${item.author.name}, ${COLLAB_V3_COPY.app.openApp}`}
      />
      <div className={styles.author}>
        <CollabV3Avatar person={item.author} size="xssm" />
        <div className={styles.authorTexts}>
          <span className={styles.authorName}>{item.author.name}</span>
          {item.author.isOrganizer ? (
            <CollabV3Badge text={COLLAB_V3_COPY.participants.host} tone="neutral" />
          ) : null}
          {item.isPinned ? <CollabV3Badge text={copy.pinned} tone="brand" /> : null}
          {item.isSelected ? (
            <CollabV3Badge text={copy.selected} tone="highlight" />
          ) : null}
          <span className={styles.date}>
            <CollabV3DateText iso={item.createdAt} kind="short" />
          </span>
        </div>
      </div>
      {label ? <span className={styles.label}>{label}</span> : null}
      {item.content ? <p className={styles.content}>{item.content}</p> : null}
      {item.link ? <CollabV3ThreadLinkRow link={item.link} /> : null}
      {item.imageUrls.length > 0 ? (
        <CollabV3ThreadMedia imageUrls={item.imageUrls} />
      ) : null}
      {item.pickedWorks.length > 0 ? (
        <CollabV3ThreadPicked works={item.pickedWorks} />
      ) : null}
      {item.execution ? (
        <CollabV3ThreadExecution execution={item.execution} />
      ) : null}
      <CollabV3ThreadReactions
        likeCount={item.likeCount}
        commentCount={item.commentCount}
      />
    </li>
  );
});

CollabV3ThreadItem.displayName = "CollabV3ThreadItem";

export default CollabV3ThreadItem;
